import React, { useMemo } from 'react';
import Drawer from '@mui/material/Drawer';
import { ArrowLeft } from 'lucide-react';
import { useAppStore } from '../../store/Store';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useNavigate } from 'react-router-dom';

import StarIcon from '../../icons/StarIcon';
import ReturnIcon from '../../icons/ReturnIcon';
import HeartIcon from '../../icons/HeartIcon';
import { BASE_URL } from '../../apis/constant';



const Tag = ({ text, color }) => {
  const baseClasses = 'absolute top-3 left-3 px-2 py-0.5 text-xs font-bold rounded-full text-white tracking-wider z-10';
  return <div className={`${baseClasses} ${color}`}>{text}</div>;
};


const PriceDisplay = ({ price, offerPrice }) => (
  <div className="flex items-center gap-3">
    <span className="text-lg sm:text-xl font-bold text-gray-900">
      {`${(offerPrice ?? price).toLocaleString('en-US', { maximumFractionDigits: 0 })}৳`}
    </span>
    {offerPrice && (
      <div className="flex items-center gap-1">
        <span className="text-xs sm:text-sm text-gray-400 line-through">
          {`${price.toLocaleString('en-US', { maximumFractionDigits: 0 })}৳`}
        </span>
      </div>
    )}
  </div>
);

const WishlistProductCard = ({ product }) => {
  const { id, name, imageUrl, price, offerPrice, tag, variants, rating, reviews } = product;

  const navigate = useNavigate();
  const { toggleWishlist, openProductDrawer, wishlistItems } = useAppStore();

  const isWishlisted = wishlistItems.some(item => item.id === id);
  const isActionDisabled = tag?.type === 'Sold Out' || tag?.type === 'comingSoon';

  // URL Slug logic from ProductCard
  const slugify = (text) =>
    text.toLowerCase().trim().replace(/\s+/g, "-").replace(/[^\w-]+/g, "");


  const handleRemove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product);
  };

  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isActionDisabled) return;

    if (variants && variants.length > 0) {
      navigate(`/shop/${id}/${slugify(name)}`);
    } else {
      openProductDrawer(product);
    }
  };

  const handleProductClick = (e) => {
    e.preventDefault();
    navigate(`/shop/${id}/${slugify(name)}`);
  };

  return (
    <div
      className="w-full bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow duration-300 ease-in-out border border-gray-100 overflow-hidden flex flex-col cursor-pointer"
      onClick={handleProductClick}
    >
      <div className="relative overflow-hidden">
        {tag && <Tag text={tag.text} color={tag.color} />}

        {/* Remove Button */}
        <button
          onClick={handleRemove}
          className="absolute top-2 right-2 z-20 p-1.5 rounded-full bg-white/20 backdrop-blur-sm text-gray-800 hover:text-red-500 transition-all duration-300 hover:scale-110"
          aria-label="Remove from wishlist"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth="2.5"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Product Image with BASE_URL */}
        <img
          src={`${BASE_URL}${imageUrl}`}
          alt={name}
          className="w-full h-auto aspect-square object-cover transition-transform duration-300 hover:scale-105"
        />
      </div>

      {/* Product Info */}
      <div className="p-3 flex flex-col flex-grow">
        <div className="text-sm sm:text-[14px] font-bold text-gray-800 truncate" title={name}>
          {name}
        </div>

        {/* Star Rating System Logic */}
        <div className="flex items-center gap-1 mt-0.5">
          <div className="flex items-center gap-[2px]">
            {[...Array(5)].map((_, index) => {
              let fillPercent = 0;
              if (rating && rating > 0) {
                const starValue = rating - index;
                if (starValue >= 0.9) fillPercent = 100;
                else if (starValue >= 0.4) fillPercent = 70;
                else if (starValue >= 0.1) fillPercent = 30;
              }
              return (
                <div key={index} className="relative w-3 h-3">
                  <StarIcon className="absolute top-0 left-0 w-3 h-3 text-gray-300" />
                  {fillPercent > 0 && (
                    <div
                      className="absolute top-0 left-0 h-3 overflow-hidden"
                      style={{ width: `${fillPercent}%` }}
                    >
                      <StarIcon className="w-3 h-3 text-yellow-400" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <span className="text-[11px] font-semibold text-gray-700">
            {reviews > 0 ? rating.toFixed(1) : ''}
          </span>
          <span className="text-[11px] text-gray-500">
            {reviews > 0 ? `(${reviews})` : '(Not Rated)'}
          </span>
        </div>

        {/* Price and Return Policy */}
        <div className="mt-auto">
          <PriceDisplay price={price} offerPrice={offerPrice} />
          <div className="flex items-center gap-1 mt-1 text-[10px] font-medium text-emerald-600">
            <ReturnIcon className="w-3 h-3 flex-shrink-0" />
            <span>3 days easy return policy</span>
          </div>
        </div>
      </div>

      {/* Action Button */}
      {/* <div className="px-3 pb-3">
        <button
          onClick={handleButtonClick}
          disabled={isActionDisabled}
          className="w-full px-4 py-2 text-xs sm:text-sm font-bold bg-gray-900 text-white rounded-lg transition duration-300 ease-in-out hover:bg-gray-700 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-gray-900"
        >
          {buttonText}
        </button>
      </div> */}
    </div>
  );
};

const EmptyWishlist = ({ onClose }) => (
  <div className="w-full h-full bg-white text-gray-900 flex flex-col items-center justify-center p-6">
    <div className="w-62 h-62 mb-1">
      <DotLottieReact
        src="https://lottie.host/99fce016-ac7c-4f2b-a0b1-0d84db2ca55f/6dcFTuE4Fa.lottie"
        loop
        autoplay
      />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-1">No Items</h3>
    <p className="text-gray-400 text-sm mb-5">Start adding products to wishlist</p>

    <button
      onClick={onClose}
      className="bg-black text-white text-sm font-medium py-2.5 px-7 rounded-lg hover:bg-gray-800 transition-all duration-200 hover:scale-105"
    >
      Browse Products
    </button>
  </div>
);

const WishlistDrawer = ({ open, onClose }) => {
  const { wishlistItems = [], wishlistCount = 0 } = useAppStore();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 450 },
          maxWidth: '450px',
          boxSizing: 'border-box'
        }
      }}
    >
      <div className="w-full h-full bg-white relative flex flex-col overflow-hidden">
        <header className="flex-shrink-0 bg-white/90 backdrop-blur-md border-b border-gray-100 px-5 py-4 flex items-center justify-between z-20">
          <button
            onClick={onClose}
            className="p-2 -ml-2 text-gray-500 hover:text-black transition-colors rounded-full hover:bg-gray-50"
          >
            <ArrowLeft size={22} />
          </button>
          <h1 className="text-base font-bold text-black uppercase tracking-wide">
            Wishlist
          </h1>
          <div className="w-8 h-8 flex items-center justify-center relative">
            <HeartIcon isFilled={true} className="text-black" />
            {wishlistItems.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-black text-white text-[9px] font-bold w-3.5 h-3.5 rounded-full flex items-center justify-center ring-2 ring-white">
                {wishlistCount}
              </span>
            )}
          </div>
        </header>

        <main className="flex-grow overflow-y-auto p-4">
          {wishlistItems.length === 0 ? (
            <EmptyWishlist onClose={onClose} />
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {wishlistItems.map((item) => (
                <WishlistProductCard
                  key={item.id}
                  product={item}
                />
              ))}
            </div>
          )}
        </main>
      </div>
    </Drawer>
  );
};

export default WishlistDrawer;